import { CANONICAL, GIFI, CANONICAL_TO_GIFI, gifiFor } from "../../../../../../constants/CanonicalCodes";

export { CANONICAL, GIFI, CANONICAL_TO_GIFI, gifiFor };

// Default depreciation rate used by the CA_01 / CA_02 cascades to estimate
// annual depreciation when no PP&E continuity schedule is available.
// Kept as both percent and fraction since both forms appear in cascades.
export const DEFAULT_DEPRECIATION_RATE_PERCENT = 20;
export const DEFAULT_DEPRECIATION_RATE_FRACTION = 0.2;

// ─── Note IDs ────────────────────────────────────────────────────────────────
//
// Keys into NOTE_TEMPLATES. Defined separately so callsites can use
// NOTE_IDS.BASIS instead of the typo-prone "n_basis" literal.

export const NOTE_IDS = {
	BASIS: "n_basis",
	POLICIES: "n_policies",
	BUSINESS: "n_business",
	GOING_CONCERN: "n_going",
	RELATED: "n_related",
	PPE: "n_capasset",
	LTD: "n_ltd",
	SHAREHOLDER: "n_shareholder",
	LEASE: "n_lease",
	CONTINGENT: "n_contingent",
	SUBSEQUENT: "n_subsequent",
	TAX: "n_tax",
};

// ─── Note Templates ──────────────────────────────────────────────────────────

export const NOTE_TEMPLATES = {
	[NOTE_IDS.BASIS]: {
		title: "Basis of Accounting",
		ref: "CSRS 4200.14(b)",
		template: function (data) {
			let framework = "an other basis of accounting as described herein";
			if (data && data.basisOfAccounting === "aspe") framework = "Accounting Standards for Private Enterprises (ASPE), Part II of the CPA Canada Handbook";
			else if (data && data.basisOfAccounting === "tax") framework = "the income tax basis of accounting, which is an Other Comprehensive Basis of Accounting (OCBOA)";
			return "This financial information has been prepared on the basis of " + framework + ", which is a basis of accounting other than Canadian generally accepted accounting principles.";
		},
	},
	[NOTE_IDS.POLICIES]: {
		title: "Significant Accounting Policies",
		ref: "ASPE 1505",
		template: function () {
			return "The following is a summary of significant accounting policies used in the preparation of this financial information:\n\n• Revenue Recognition: Revenue is recognized when the significant risks and rewards of ownership have been transferred to the customer.\n\n• Capital Assets: Capital assets are recorded at cost. Amortization is provided using the [method] over [useful life].\n\n• Income Taxes: The entity accounts for income taxes using the [taxes payable / future income taxes] method.";
		},
	},
	[NOTE_IDS.BUSINESS]: {
		title: "Description of Business",
		ref: "CSRS 4200",
		fields: function (data) {
			let province = (data && data.province) || "";
			let actMap = {
				"Ontario": "Ontario Business Corporations Act",
				"British Columbia": "British Columbia Business Corporations Act",
				"Alberta": "Alberta Business Corporations Act",
				"Quebec": "Quebec Business Corporations Act",
				"Manitoba": "Manitoba Corporations Act",
				"Saskatchewan": "Saskatchewan Business Corporations Act",
				"Nova Scotia": "Nova Scotia Companies Act",
				"New Brunswick": "New Brunswick Business Corporations Act",
				"Newfoundland and Labrador": "Newfoundland and Labrador Corporations Act",
				"Prince Edward Island": "Prince Edward Island Companies Act",
			};
			// Also try matching partial / case-insensitive
			let defaultAct = actMap[province] || "";
			if (!defaultAct && province) {
				for (let key in actMap) {
					if (key.toLowerCase() === province.toLowerCase() || province.toLowerCase().includes(key.toLowerCase())) {
						defaultAct = actMap[key];
						break;
					}
				}
			}
			return [
				{ id: "jurisdiction", label: "Incorporated", type: "select", options: ["provincially", "federally"], default: "provincially" },
				{ id: "act", label: "Under the", type: "text", placeholder: "e.g. Ontario Business Corporations Act", default: defaultAct },
				{ id: "date", label: "On", type: "text", placeholder: "e.g. January 15, 2020" },
				{ id: "description", label: "And", type: "text", placeholder: "e.g. provides consulting services to small businesses" },
			];
		},
		template: function (data) {
			let entityName = (data && data.entityName) || "[Entity Name]";
			let jurisdiction = (data && data.jurisdiction) || (data && data.incorporationJurisdiction) || "provincially";
			let act = (data && data.act) || (data && data.incorporationAct) || "[applicable act]";
			let date = (data && data.date) || (data && data.incorporationDate) || "[date]";
			let description = (data && data.description) || (data && data.businessDescription) || "provides [describe products/services]";
			return entityName + " was incorporated " + jurisdiction + " under the " + act + " on " + date + " and " + description + ".";
		},
	},
	[NOTE_IDS.GOING_CONCERN]: {
		title: "Going Concern",
		ref: "ASPE 1400",
		template: function () { return "There is significant doubt about the entity's ability to continue as a going concern. Management has identified the following conditions that raise this doubt: [describe conditions]. Management's plans to address these conditions include: [describe plans]."; },
	},
	[NOTE_IDS.RELATED]: {
		title: "Related Party Transactions",
		ref: "ASPE 3840",
		template: function () { return "During the year, the entity entered into the following transactions with related parties:\n\n• [Name / relationship]: [Description of transaction] — $[amount]\n\nThese transactions were made in the normal course of operations and measured at the exchange amount."; },
	},
	[NOTE_IDS.PPE]: {
		title: "Capital Asset Continuity Schedule",
		ref: "ASPE 3061",
		template: function () { return "Capital assets consist of the following:\n\n[Insert capital asset continuity schedule showing cost, accumulated amortization, and net book value by asset category]\n\nAmortization expense for the year was $[amount] (prior year — $[amount])."; },
	},
	[NOTE_IDS.LTD]: {
		title: "Long-term Debt",
		ref: "ASPE 3210",
		template: function () { return "Long-term debt consists of the following:\n\n[Description, interest rate, maturity, security]\n\nFuture minimum principal payments are as follows:\n• Within one year: $\n• One to five years: $\n• After five years: $"; },
	},
	[NOTE_IDS.SHAREHOLDER]: {
		title: "Shareholder / Director Loans",
		ref: "ASPE 3840",
		template: function () { return "Amounts due to / from shareholders and directors are non-interest-bearing, unsecured, and have no fixed terms of repayment. These amounts are subordinated to other creditors."; },
	},
	[NOTE_IDS.LEASE]: {
		title: "Operating Lease Commitments",
		ref: "ASPE 3065",
		template: function () { return "The entity has the following future minimum lease payments under non-cancellable operating leases:\n\n• Within one year: $\n• One to five years: $\n• After five years: $\n\nTotal: $"; },
	},
	[NOTE_IDS.CONTINGENT]: {
		title: "Contingent Liabilities",
		ref: "ASPE 3290",
		template: function () { return "The entity is involved in the following contingencies:\n\n[Describe the nature of any pending litigation, guarantees, or other contingencies and the estimated financial exposure, if determinable.]"; },
	},
	[NOTE_IDS.SUBSEQUENT]: {
		title: "Subsequent Events",
		ref: "ASPE 3820",
		template: function () { return "Subsequent to the balance sheet date, the following material event(s) occurred:\n\n[Describe the event(s) and estimated financial impact, if applicable.]"; },
	},
	[NOTE_IDS.TAX]: {
		title: "Income Taxes",
		ref: "ASPE 3465",
		template: function () { return "The provision for income taxes differs from the amount computed by applying the combined federal and provincial statutory rate of [X]% due to the following:\n\n[Insert income tax reconciliation or future income tax details as applicable.]"; },
	},
};

// ─── BS Grouping Categories ──────────────────────────────────────────────────


export const CURRENT_ASSET_CATS = new Set(["current-assets"]);
export const NONCURRENT_ASSET_CATS = new Set(["capital-assets", "other-assets"]);
export const CURRENT_LIAB_CATS = new Set(["current-liabilities"]);
export const LT_LIAB_CATS = new Set(["long-term-liabilities"]);
export const EQUITY_CATS = new Set(["equity"]);

export const IS_ORDER = ["revenue", "cogs", "expenses", "other-income", "tax"];

// Map AFS section categories to lead sheet WP reference numbers
export const WP_REF_BY_CATEGORY = {
	"current-assets": "A-1",
	"capital-assets": "A-5",
	"other-assets": "A-7",
	"current-liabilities": "L-1",
	"long-term-liabilities": "L-4",
	"equity": "E-1",
	"revenue": "R-1",
	"other-income": "R-1",
	"cogs": "X-1",
	"expenses": "X-2",
	"tax": "X-3",
};

// ─── AJE Templates ───────────────────────────────────────────────────────────

export const AJE_TEMPLATE_GROUPS = [
	{
		label: "Year-end close",
		templates: [
			{ label: "Depreciation", description: "To record depreciation expense", debitGifi: GIFI.DEPRECIATION_EXPENSE, creditGifi: GIFI.ACCUMULATED_DEPRECIATION },
			{ label: "Amortization", description: "To record amortization of intangible assets", debitGifi: GIFI.AMORTIZATION_EXPENSE, creditGifi: GIFI.ACCUMULATED_AMORTIZATION },
			{ label: "Tax Provision", description: "To record income tax provision", debitGifi: GIFI.INCOME_TAX_PROVISION, creditGifi: GIFI.DEFERRED_REVENUE },
		],
	},
	{
		label: "Adjustments",
		templates: [
			{ label: "Accrued Expenses", description: "To record accrued liabilities at year-end", creditGifi: GIFI.ACCRUED_EXPENSES, debitHint: "Pick the expense account being accrued." },
			{ label: "Prepaid Amortization", description: "To record amortization of prepaid expenses", debitHint: "Pick the expense account this prepaid relates to.", creditHint: "Pick the prepaid asset being amortized." },
			{ label: "Bad Debt", description: "To record bad debt expense / allowance adjustment", debitGifi: GIFI.BAD_DEBT, creditGifi: GIFI.ALLOWANCE_FOR_DOUBTFUL },
			{ label: "Inventory Adjustment", description: "To adjust inventory to actual count", debitHint: "Direction depends on whether inventory was over- or under-stated.", creditHint: "Direction depends on whether inventory was over- or under-stated." },
		],
	},
];


// ─── Tab Labels ──────────────────────────────────────────────────────────────

export const TAB_LABELS = {
	bs: "Balance Sheet",
	is: "Income Statement",
	tb: "Trial Balance",
	bookkeeping: "Bookkeeping",
};

export const TABS = [
	{ id: "bookkeeping", label: "Bookkeeping" },
	{ id: "tb",          label: "Trial Balance" },
	{ id: "report",      label: "Compilation Report" },
	{ id: "bs",          label: "Balance Sheet" },
	{ id: "is",          label: "Income Statement" },
	{ id: "notes",       label: "Notes" },
	{ id: "summary",     label: "Summary" },
	{ id: "review",      label: "Review" },
	{ id: "leadsheets", label: "Lead Sheets" },
	{ id: "documentation", label: "Documentation" },
	{ id: "agent_mode",  label: "Agent Mode" },
	{ id: "compliance",  label: "Compliance" },
	{ id: "preview",     label: "Preview" },
	{ id: "package",     label: "Year-End Package" },
];
